import { Component, OnInit, Inject, LOCALE_ID } from '@angular/core';

import { DatePipe } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { JobService } from '../services/job.service';
import { Job } from '../../shared/models/job.interface';
import { JobAreaSummary } from '../models/job.area.summary.interface';
import { ChangeOrder } from '../../shared/models/change.order.interface';
import { WorkOrderType } from '../../shared/models/work.order.type.interface';
import { WorkOrder } from '../../shared/models/work.order.interface';
import { JobCostAreaEdit, JobCostEdit } from '../models/job.cost.edit.interface';
import { QuoteBase } from '../../shared/models/quote.base.interface';
import { Crew } from '../../shared/models/crew.interface';
import { RolePermission } from '../../shared/models/role.permission.enum';
import { DeviceDetectorService } from 'ngx-device-detector';
import { AbstractBiditComponent } from '../../shared/controls/abstract.bidit.component';
import { QuoteService } from '../../quote/services/quote.service';
import { FileService } from '../../shared/services/file.service';
import { QuoteTemplateDialog } from '../../shared/controls/quote/create-quote-template/create-quote-template.dialog';
import { ChangeOrderDialog, ChangeOrderDialogData } from '../change-order-dialog/change.order.dialog';
import { PrintOptionsDialog } from '../../shared/controls/print-options-dialog/print.options.dialog';
import { WorkOrderDialog, WorkOrderDialogData } from '../../shared/controls/work-order-dialog/work.order.dialog';
import { JobCostItemDialog } from '../job-cost-item-dialog/job-cost-item.dialog';
import { JobAdditionalItem } from '../../shared/models/job.additional.item.interface';
import { JobStatus } from '../../shared/models/job.status.enum';
import { PrintLanguageDialog } from '../../shared/controls/print-language-dialog/print.language.dialog';
import { ChangeOrderPrintDialog } from '../change-order-print-dialog/change.order.print.dialog';
import { NgForm } from '@angular/forms';
import { MaterialsListDialogComponent } from '../../shared/controls/materials-list-dialog/materials-list-dialog.component';
import { JobCustomItem } from '../../shared/models/job.custom.item.interface';
import { JobCustomWorkAreaDialog } from '../job-custom-work-area-dialog/job-custom-work-area-dialog';
import { Customer } from '../../shared/models/customer.interface';
import { ArrivalNotificationDialog } from '../arrival-notification-dialog/arrival-notification-dialog';
import { CalendarUtils } from 'angular-calendar';

export interface StatusDialogData {
  quote_ID: number;
  quote_IsLost: boolean;
  quote_LostNote: string;
}

export interface JobAddressData {
  states: string[];
  job: Job;
}

export interface WorkOrderData {
  projectName: string;
  customer: Customer;
}

@Component({
  selector: 'app-summary',
  templateUrl: './summary.component.html',
  styleUrls: ['./summary.component.scss']
})
export class SummaryComponent extends AbstractBiditComponent {
  job: Job = {} as Job;
  areaSummaries: JobAreaSummary[];
  jobCustomItems: JobCustomItem[];
  changeOrderId: number;
  states: string[];

  lostStatus: string;

  checkAll: boolean;
  showChangeOrder: boolean = false;
  isFieldTech: boolean = false;

  times: string[];
  crews: Crew[];
  workOrderTypes: WorkOrderType[];

  canEdit: boolean = true;
  canViewQuoteAnalysis: boolean = false;
  canAddWorkOrder: boolean = false;
  canEditWorkOrder: boolean = false;
  canAddChangeOrder: boolean = false;
  canEditChangeOrder: boolean = false;
  allowPrint: boolean = false;
  showQuoteAnalysis: boolean = false;

  constructor(@Inject(LOCALE_ID) private locale: string,
    private datepipe: DatePipe, private dialog: MatDialog, private jobService: JobService,
    private quoteService: QuoteService, private fileService: FileService,
    private deviceDetectorService: DeviceDetectorService, private route: ActivatedRoute) {
    super(deviceDetectorService);
  }

  init() {
    this.canEdit = JobService.hasPermission(RolePermission.EditJob);
    this.isFieldTech = JobService.hasPermission(RolePermission.FieldTechnician);
    this.showQuoteAnalysis = JobService.hasPermission(RolePermission.QuoteAnalysis);
    this.canAddChangeOrder = JobService.hasPermission(RolePermission.AddChangeOrder);
    this.canEditChangeOrder = JobService.hasPermission(RolePermission.EditChangeOrder);
    this.canAddWorkOrder = JobService.hasPermission(RolePermission.AddWorkOrder);
    this.canEditWorkOrder = JobService.hasPermission(RolePermission.EditWorkOrder);
    this.allowPrint = JobService.hasPermission(RolePermission.ShowLineItemPricing) && JobService.hasPermission(RolePermission.ShowCostBreakdown) &&
                      JobService.hasPermission(RolePermission.ViewEditMargins);

    this.route.paramMap.subscribe(params => {
      let jobId = params.get('jobId');
      this.loadJob(parseInt(jobId));
    });

    this.jobService.getCrews()
      .subscribe({
        next: (data) => {
          this.crews = data;
        }
      });

    this.jobService.getWorkOrderTypes()
      .subscribe({
        next: (data) => {
          this.workOrderTypes = data;
        }
      });

    this.quoteService.getStates()
      .subscribe({
        next: (data) => {
          this.states = data;
        }
      });

    this.times = new Array();
    for (let hour = 6; hour < 22; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        let timeValue = new Date(1970, 0, 1, hour, minute, 0);
        this.times.push(this.datepipe.transform(timeValue, 'hh:mm a'));
      }
    }
    this.times.push('10:00 PM');
  }
  get noteLabel(): string {
    if (this.locale == 'es')
      //var noteLbl = $localize`Notes`;
      return `${$localize`Notes`} (${this.job.notes?.length})`;

    return `Notes (${this.job.notes?.length})`;
  }

  get documentLabel(): string {
    if (this.locale == 'es')
      return `${$localize`Documents`} (${this.job.documents?.length})`;

    return `Documents (${this.job.documents?.length})`;
  }

  editAddress(): void {
    const dialogRef = this.dialog.open(JobAddressDialog, {
      width: '500px',
      data: {
        job: this.job,
        states: this.states
      } as JobAddressData
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed');
      if (result) {
        let project = result as Job;

        this.job.quote_Name = project.quote_Name;
        this.job.quote_Address1 = project.quote_Address1;
        this.job.quote_Address2 = project.quote_Address2;
        this.job.quote_City = project.quote_City;
        this.job.quote_State = project.quote_State;
        this.job.quote_Postal = project.quote_Postal;
      }
    });
  }

  loadJob(jobId: number) {
    this.isRequesting = true;

    this.jobService.getJob(jobId)
      .subscribe({
        next: (data) => {
          this.job = data.job;
          this.lostStatus = (this.job.quote_IsLost ? 'C' : (this.job.jobManuallyCompleted || this.job.jobStatus === JobStatus.Complete) ? 'CP' :'');
          this.areaSummaries = this.summarizeAreas(data.jobAreaSummaries);
          this.jobCustomItems = this.getJobItemWorkOrders(data.jobCustomItems, data.job.workOrders);
          this.isRequesting = false;
        }
      });
  }

  summarizeAreas(areaSummaries: JobAreaSummary[]): JobAreaSummary[] {
    for (let area of areaSummaries) {
      for (let quoteArea of area.quoteAreas) {

        if (quoteArea.acceptedItem.area_ActualSqrFtg == null) quoteArea.acceptedItem.area_ActualSqrFtg = quoteArea.acceptedItem.area_SqrFtg;

        for (let workOrder of this.job.workOrders) {
          let workOrderArea = workOrder.workOrderAreas.find((woa) => {
            return woa.quoteArea_ID == quoteArea.quoteArea_ID;
          });

          if (workOrderArea != null) {
            if (quoteArea.workOrders == null)
              quoteArea.workOrders = new Array();

            quoteArea.workOrders.push(workOrder);
          }
        }
      }
    }

    return areaSummaries;
  }

  getJobItemWorkOrders(jobCustomItems: JobCustomItem[], jobWorkOrders: WorkOrder[]) {
    for (let item of jobCustomItems) {

      jobWorkOrders.forEach(wo => wo.workOrderCustomItems.forEach((woci) => {
        if (woci.jobCustomItem_ID === item.jobCustomItem_ID) {
          item.workOrders.push(wo);
        }
      }));

    }

    return jobCustomItems;
  }

  get acknowledgementLabel(): string {
    if (!this.job) return;

    if (this.locale == 'es')
      return `${$localize`Acknowledgements`} (${this.job.quoteAcknowledgements.length})`;
    return `Acknowledgements (${this.job.quoteAcknowledgements.length})`;
  }

  jobNotesUpdated(quote: QuoteBase) {
    this.loadJob(quote.quote_ID);
  }

  jobStatusChanged() {
    if (this.lostStatus == 'C') {
      this.openStatusDialog();
      return;
    }

    this.isRequesting = true;
    this.job.quote_IsLost = false;

    if (this.lostStatus === 'CP') {

      if (this.job.jobStatus !== JobStatus.Canceled && this.job.jobStatus !== JobStatus.Complete) {
        var proceed = confirm($localize`There are open work orders or accepted quote areas with no work order,  are you sure you want to complete?`);
        this.isRequesting = false;
        this.loadJob(this.job.quote_ID); // refresh status at top of screen
        if (!proceed) return;
      }

      this.jobService.updateJobManuallyCompleted(this.job.quote_ID, this.job)
      .subscribe({
        next: (data) => {
          this.loadJob(this.job.quote_ID); // refresh status at top of screen
          return;
        },
        error: (error: string) => this.onError(error)
      });
    }

    if (this.lostStatus === '') {
    this.jobService.updateJobLostStatus(this.job.quote_ID, this.job)
      .subscribe({
        next: (data) => {
          this.loadJob(this.job.quote_ID); // refresh status at top of screen
        },
        error: (error: string) => this.onError(error)
      });
    }
  }

  openStatusDialog(): void {
    const dialogRef = this.dialog.open(JobStatusDialog, {
      width: '500px',
      data: {
        quote_ID: this.job.quote_ID,
        quote_IsLost: this.job.quote_IsLost,
        quote_LostNote: this.job.quote_LostNote
      }
    });


    dialogRef.afterClosed().subscribe({
      next: (result) => {
        console.log('The dialog was closed');
        if (result) {
          result.quote_IsLost = true;

          this.isRequesting = true;

          this.jobService.updateJobLostStatus(this.job.quote_ID, result)
            .subscribe({
              next: (data) => {
                this.isRequesting = false;
                this.job.jobStatus = JobStatus.Canceled;
                this.job.quote_IsLost = true;
              },
              error: (error: string) => this.onError(error)
            });
        } else {
          this.lostStatus = '';
        }
      }
    });
  }

  changeOrderUpdated(changeOrder: ChangeOrder) {
    this.loadJob(this.job.quote_ID);
  }

  editWorkOrder(workOrder: WorkOrder) {
    this.workOrderDialog(workOrder.workOrder_ID);
  }

  newChangeOrder() {
    let dialogData = {
      changeOrder: {} as ChangeOrder
    } as ChangeOrderDialogData;

    dialogData.changeOrder.quote_ID = this.job.quote_ID;

    const dialogRef = this.dialog.open(ChangeOrderDialog, {
      width: '700px',
      data: dialogData,
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed');
      if (result) {
        this.loadJob(this.job.quote_ID);
        this.editChangeOrder(result);
      }
    });
  }

  editChangeOrder(changeOrderId: number) {
    this.changeOrderId = changeOrderId;
    this.showChangeOrder = true;
  }

  checkSendAllowSendNotifications() {
    let currentDate = this.datepipe.transform(new Date, 'yyyy-MM-ddT00:00:00');
    let filteredWorkOrders = this.job.workOrders.filter(wo => (currentDate >= this.datepipe.transform(wo.workOrder_Start, 'yyyy-MM-ddT00:00:00')) && currentDate <= this.datepipe.transform(wo.workOrder_Stop, 'yyyy-MM-ddT00:00:00'));
    const canSend = filteredWorkOrders.length > 0 ? true : false;
    return canSend;
  }

  sendNotification() {
    
    let dialogData = {
      projectName: this.job.quote_Name,
      customer: this.job.customer

    } as WorkOrderData;

    dialogData.customer.dealer_ID = this.job.dealer_ID;
    const dialogRef = this.dialog.open(ArrivalNotificationDialog, {
      width: '700px',
      data: dialogData,
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed');
      if (result) {
        
      }
    });
  }

  printChangeOrder(changeOrder: ChangeOrder) {
    const dialogRef = this.dialog.open(ChangeOrderPrintDialog, {
      data: {
        isInvoice: false
      }
    });
    dialogRef.afterClosed().subscribe({
      next: (result) => {
        if (!result) return;
        this.isRequesting = true;

        this.jobService.printChangeOrder(changeOrder.changeOrder_ID, result)
          .subscribe({
            next: (x) => {
              this.isRequesting = false;
              this.fileService.downloadFile(x, 'ChangeOrderContract.pdf');
            },
            error: (error: string) => this.onError(error)
          });
      }
    });
  }

  printInvoice(changeOrderId: number, combinedInvoice: boolean) {
    const dialogRef = this.dialog.open(ChangeOrderPrintDialog, {
      data: {
        isInvoice: true
      }
    });
    dialogRef.afterClosed().subscribe({
      next: (result) => {
        if (!result) return;
        this.isRequesting = true;
        result.combinedInvoice = combinedInvoice;

        if (combinedInvoice) {
          changeOrderId = this.job.currentChangeOrder_ID;
        }

        this.jobService.printInvoice(this.job.quote_ID, changeOrderId, result)
          .subscribe({
            next: (x) => {
              this.isRequesting = false;
              this.fileService.downloadFile(x, 'Invoice.pdf');
            },
            error: (error: string) => this.onError(error)
          });
      }
    });
  }

  toggleChangeOrderDisplay(event: any) {
    this.showChangeOrder = false;
  }

  quoteAnalysis(): void {
    const dialogRef = this.dialog.open(PrintLanguageDialog);
    dialogRef.afterClosed().subscribe({
      next: (result) => {
        if (!result) return;
        this.isRequesting = true;

        this.quoteService.quoteAnalysis(this.job.quote_ID, this.job.currentChangeOrder_ID, result)
          .subscribe({
            next: (x) => {
              this.isRequesting = false;
              this.fileService.downloadFile(x, 'QuoteAnalysis.pdf');
            },
            error: (error: string) => this.onError(error)
          });
      }
    });
  }

  materialList(): void {
    const dialogRef = this.dialog.open(MaterialsListDialogComponent,
      {
        data: {
          quote_ID: this.job.quote_ID,
          assignedToEmail: this.job.quoteAssignments[0]?.assignedTo.email,
          dealer_IDIBranchEmail: this.job.idiBranch_Email
        }
      });

    dialogRef.afterClosed().subscribe({
      next: (result) => {
        if (!result) return;
        this.isRequesting = true;

        this.quoteService.materialList(this.job.quote_ID, result.lang, result.showJobAddress)
          .subscribe({
            next: (x) => {
              this.isRequesting = false;
              this.fileService.downloadFile(x, 'MaterialList.pdf');
            },
            error: (error: string) => this.onError(error)
          });
      }
    });
  }

  openTemplateDialog(): void {
    const dialogRef = this.dialog.open(QuoteTemplateDialog, {
      width: '500px',
      data: {
        quote_ID: this.job.quote_ID
      }
    });

    dialogRef.afterClosed().subscribe({
      next: (result) => {
        console.log('The dialog was closed');
        if (result) {
          window.alert($localize`Quote template created`);
        }
      }
    });
  }

  customItem(): void {
    const dialogRef = this.dialog.open(JobCostItemDialog, {
      width: '850px',
      data: {
        quote_ID: this.job.quote_ID
      }
    });

    dialogRef.afterClosed().subscribe({
      next: (result) => {
        console.log('The dialog was closed');
        if (result) {
          this.job.jobAdditionalItems.push(result);
        }
      }
    });
  }

  removeCustomItem(customItem: JobAdditionalItem): void {
    let proceed = confirm($localize`Remove this additional item?`);
    if (!proceed) return;

    let index = this.job.jobAdditionalItems.indexOf(customItem);
    this.job.jobAdditionalItems.splice(index, 1);
  }

  removeJobCustomItem(customItem: JobCustomItem): void {
    let proceed = confirm($localize`Remove this additional item?`);
    if (!proceed) return;

    let index = this.job.jobCustomItems.indexOf(customItem);
    this.job.jobCustomItems.splice(index, 1);
  }

  addCustomWorkOrderItem(): void {
    const dialogRef = this.dialog.open(JobCustomWorkAreaDialog, {
      width: '850px',
      data: {
        quote_ID: this.job.quote_ID,
        customItem_ID: null
      }
    });

    dialogRef.afterClosed().subscribe({
      next: (result) => {
        console.log('The dialog was closed');
        this.loadJob(this.job.quote_ID);
      }
    });
  }

  editCustomItem(jobCustomItem_ID: number): void {
    const dialogRef = this.dialog.open(JobCustomWorkAreaDialog, {
      width: '850px',
      data: {
        quote_ID: this.job.quote_ID,
        customItem_ID: jobCustomItem_ID
      }
    });

    dialogRef.afterClosed().subscribe({
      next: (result) => {
        console.log('The dialog was closed');
        this.loadJob(this.job.quote_ID);
      }
    });
  }

  removeCustomWorkOrderItem(customItem: JobCustomItem): void {
    let proceed = confirm($localize`Remove this custom item?`);
    if (!proceed) return;

    //let index = this.job.wor.jobAdditionalItems.indexOf(customItem);
    //this.work.jobAdditionalItems.splice(index, 1);
  }

  deleteChangeOrder(changeOrderId: number): void {
    let proceed = confirm($localize`Delete this change order?`);
    if (!proceed) return;
    this.jobService.deleteChangeOrder(changeOrderId).subscribe({
      next: (x) => {
        this.loadJob(this.job.quote_ID);
        this.isRequesting = false;

      },
      error: (error: string) => this.onError(error)
    });

  }

  updateJobCosting(): void {
    this.isRequesting = true;

    let costEdit: JobCostEdit = {
      quoteAreas: new Array(),
      additionalItems: this.job.jobAdditionalItems,
      customItems: this.job.jobCustomItems
    };

    this.areaSummaries.forEach(as => {
      as.quoteAreas.forEach(qa => {
        costEdit.quoteAreas.push({
          quoteArea_ID: qa.quoteArea_ID,
          quoteAreaItem_ID: qa.acceptedItem.quoteAreaItem_ID,
          jobCost_ActualSqrFtg: qa.acceptedItem.area_ActualSqrFtg,
          jobCost_ActualUsage: qa.acceptedItem.area_ActualItemUsage
        } as JobCostAreaEdit);

        qa.acceptedItem.accessories.forEach(acc => {
          costEdit.quoteAreas.push({
            quoteArea_ID: qa.quoteArea_ID,
            quoteAreaItem_ID: qa.acceptedItem.quoteAreaItem_ID,
            accessory_ID: acc.accessory_ID,
            jobCost_ActualUsage: acc.accessory_ActualUsage
          } as JobCostAreaEdit);
        });
      });
    });

    this.jobService.saveJobCosting(this.job.quote_ID, costEdit)
      .subscribe({
        next: (x) => {
          this.isRequesting = false;
          window.alert($localize`Save complete`);
        },
        error: (error: string) => this.onError(error)
      });
  }

  jobCostCalculator(): void {
    this.isRequesting = true;

    this.jobService.jobCostCalculator(this.job.quote_ID)
      .subscribe({
        next: (x) => {
          this.isRequesting = false;
          this.fileService.downloadFile(x, 'JobCostCalculator.xlsx');
        },
        error: (error: string) => this.onError(error)
      });
  }

  openPrintOptionsDialog(): void {
    const dialogRef = this.dialog.open(PrintOptionsDialog, {
      width: '500px',
      data: {
        quote_ID: this.job.quote_ID,
        changeOrder_ID: this.job.currentChangeOrder_ID,
        quote_ContractNotes: this.job.quote_ContractNotes
      }
    });

    dialogRef.afterClosed().subscribe({
      next: (result) => {
        console.log('The dialog was closed');
        if (result) {
          this.job.quote_ContractNotes = result.quote_ContractNotes;

          window.alert($localize`Quote Printed`);
        }
      }
    });
  }

  printQuote(changeOrderId: number): void {
    const dialogRef = this.dialog.open(ChangeOrderPrintDialog, {
      data: {
        isInvoice: false
      }
    });
    dialogRef.afterClosed().subscribe({
      next: (result) => {
        if (!result) return;
        this.isRequesting = true;

        this.quoteService.printQuote(this.job.quote_ID, changeOrderId, result)
          .subscribe({
            next: (x) => {
              this.isRequesting = false;
              this.fileService.downloadFile(x, 'QuoteContract.pdf');
            },
            error: (error: string) => this.onError(error)
          });
      }
    });
  }

  addWorkOrder() {
    this.workOrderDialog(null);
  }

  workOrderDialog(workOrderId: number) {
    let dialogData = {
      quoteId: this.job.quote_ID,
      workOrderId: workOrderId,
      defaultWorkOrderStart: workOrderId === null ? this.job.defaultWorkOrderStartTime : null,
      defaultWorkOrderStop: workOrderId === null ? this.job.defaultWorkOrderStopTime : null,

      times: this.times,
      crews: this.crews,
      workOrderTypes: this.workOrderTypes
    } as WorkOrderDialogData

    const dialogRef = this.dialog.open(WorkOrderDialog, {
      width: '700px',
      data: dialogData,
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe({
      next: (result) => {
        console.log('The dialog was closed');
        if (result) {
          this.loadJob(this.job.quote_ID);
        }
      }
    });
  }

}



@Component({
  selector: 'job.status.dialog',
  templateUrl: './job.status.dialog.html',
})
export class JobStatusDialog {

  constructor(
    public dialogRef: MatDialogRef<JobStatusDialog>,
    @Inject(MAT_DIALOG_DATA) public data: StatusDialogData) { }

  saveStatus(form: NgForm): void {
    if (!form.valid) return;

    this.dialogRef.close(this.data);
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

}

@Component({
  selector: 'job.address.dialog',
  templateUrl: './job.address.dialog.html',
})
export class JobAddressDialog {

  project: Job;
  message: string;

  constructor(
    public dialogRef: MatDialogRef<JobStatusDialog>,
    @Inject(MAT_DIALOG_DATA) public data: JobAddressData,
    private jobService: JobService) {

    this.project = Object.assign({}, data.job);

  }

  saveAddress(form: NgForm): void {
    this.message = '';
    if (!form.valid) return;

    this.jobService.saveJobAddress(this.project)
      .subscribe({
        next: (x) => {
          this.dialogRef.close(this.project);
        },
        error: (error: string) => {
          this.message = error;
        }
      });
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

}
